import './feed-header.scss';
import { useTranslation } from 'react-i18next';
import { Button, IconButton } from '@socialchorus/shared-ui-components';
import { useLinkNavigation } from '../../../common/use-link-navigation';
import {
  trackChannelViewClick,
  trackFeedViewAllClick,
} from '../../../models/analytics';

export type FeedHeaderProps = {
  id: string;
  title?: string;
  viewAllLink?: string;
  viewAllText?: string;
  viewAllContext?: string;
  description?: string;
  isTopic: boolean;
  analyticsData: IAnalyticsData;
  /** Mobile module treatment: the header shrinks to a chevron because the
   * mobile layouts repeat "view all" as a footer button below. */
  isMobileModule?: boolean;
};

export default function FeedHeader({
  id,
  title,
  viewAllLink,
  viewAllText,
  viewAllContext,
  description,
  analyticsData,
  isTopic,
  isMobileModule,
}: FeedHeaderProps) {
  const { t } = useTranslation();
  const linkNavigation = useLinkNavigation();
  const handleClick = () => {
    if (isTopic) {
      trackChannelViewClick(analyticsData);
    } else {
      trackFeedViewAllClick(analyticsData);
    }
  };

  const resolvedViewAllText = viewAllText || t('landing.see_all');
  // aria-label overrides the accessible name from `label`. Compose
  // the action verb with the section context so screen readers
  // announce e.g. "View all From the Community" instead of just
  // "From the Community".
  const viewAllAriaLabel = viewAllContext
    ? `${resolvedViewAllText} ${viewAllContext}`
    : undefined;

  return (
    <header className="feed-header">
      <div className="feed-header__first-row">
        <h2 id={`${id}--title`} className="feed-header__title">
          {title}
        </h2>
        {viewAllLink &&
          (isMobileModule ? (
            <IconButton
              iconName="chevron_right"
              size="compact"
              className="feed-header__view-all-btn"
              href={linkNavigation(viewAllLink)}
              onClick={handleClick}
              label={resolvedViewAllText}
              aria-label={viewAllAriaLabel}
            />
          ) : (
            <Button
              variant="outline"
              size="compact"
              className="feed-header__view-all-btn"
              href={linkNavigation(viewAllLink)}
              onClick={handleClick}
              label={resolvedViewAllText}
              aria-label={viewAllAriaLabel}
            />
          ))}
      </div>
      {description && (
        <h3 id={`${id}--description`} className="feed-header__description">
          {description}
        </h3>
      )}
    </header>
  );
}

type FeedViewAllFooterProps = {
  viewAllLink: string;
  viewAllText?: string;
  viewAllContext?: string;
  isTopic: boolean;
  analyticsData: IAnalyticsData;
};

export function FeedViewAllFooter({
  viewAllLink,
  viewAllText,
  viewAllContext,
  isTopic,
  analyticsData,
}: FeedViewAllFooterProps) {
  const { t } = useTranslation();
  const linkNavigation = useLinkNavigation();
  const handleClick = () => {
    if (isTopic) {
      trackChannelViewClick(analyticsData);
    } else {
      trackFeedViewAllClick(analyticsData);
    }
  };

  const resolvedViewAllText = viewAllText || t('landing.see_all');
  const viewAllAriaLabel = viewAllContext
    ? `${resolvedViewAllText} ${viewAllContext}`
    : undefined;

  return (
    <div className="feed-header__view-all-footer">
      <Button
        variant="outline"
        className="feed-header__view-all-btn"
        href={linkNavigation(viewAllLink)}
        onClick={handleClick}
        label={resolvedViewAllText}
        aria-label={viewAllAriaLabel}
      />
    </div>
  );
}
